/**
 * Consultation and WebSocket Types
 *
 * Type definitions for consultations, transcriptions, vitals, and WebSocket communications.
 */

// Ayurveda types are defined in the Ayurveda module. Imported here for local
// use in interfaces below, and re-exported for backward compatibility.
import type {
  ReferenceSlokaItem,
  AyurvedaData,
  ClinicalAssessmentEntry,
  ClinicalAssessmentParam,
  ClinicalAssessmentSource,
  RegistrationInfo,
  PersonalHistory,
  PrescribedMedicine,
} from '@/ayurveda/types/ayurveda';
import type { InvestigationEntry } from '@/lib/types/clinicalText';

/**
 * Streaming message from transcription/CDSS
 */
export interface StreamingMessage {
  type: 'transcription' | 'suggestion' | 'error';
  text: string;
  is_partial?: boolean;
  context_state?: string;
  speaker?: 'patient' | 'doctor';
}

/**
 * CDSS Data from streaming endpoint
 */
export interface StreamingCDSSData {
  possible_diagnoses?: string[];
  confirmed_diagnoses?: string[];
  follow_up_questions?: string[];
  additional_suggestions?: string[];
  suggested_medications?: string[];
  prescribed_medications?: string[];
  current_medications?: string[];
  symptoms?: string[];
  allergies?: string[];
  ordered_investigations?: (string | InvestigationEntry)[];
  diet_recommendations?: string[];
  lifestyle_recommendations?: string[];
  viruddha_ahaara?: string[];
  family_history?: string[];
  known_diseases?: string[];
  commonly_eaten_food?: string[];
  suggestive_slokas?: ReferenceSlokaItem[];
  suggestive_slokas_update?: boolean;
  /** Astha Sthaana Pareeksha / Prakruthi/Dosha/Aama / Diagnosis / Treatment
   *  chip values — full-replace array each turn, see ClinicalAssessmentEntry. */
  clinical_assessment?: ClinicalAssessmentEntry[];
  clinical_assessment_update?: boolean;
  /** Structured medicines table, full-replace each turn — see
   *  CDSSData.prescribed_medicines in LiveKitClient.ts for the same field. */
  prescribed_medicines?: PrescribedMedicine[];
  /** Live location → weather + regional-diet delta (Environmental Profile
   *  panel), published once a debounced background lookup resolves after the
   *  patient states their city. Snake_case keys match the backend directly. */
  desa_information?: {
    city?: string;
    state?: string;
    season?: string;
    desa_type?: string;
    temperature?: number;
    humidity?: number;
    staple_diet?: string;
    regional_diet?: {
      grains?: string;
      pulses?: string;
      fruits?: string;
      veg?: string;
      dairy?: string;
      spice_level?: number;
    };
  };
  desa_information_update?: boolean;
  /** Live Personal History delta, published directly on change — drives
   *  PatientInformationCard's Personal History fields (including the
   *  addiction icon highlighting). No `diet` key — that's sourced
   *  separately. */
  personal_history?: {
    appetite?: string;
    sleep?: string;
    exercise?: string;
    bowel?: string;
    bladder?: string;
    addiction?: string;
    menstrual?: string;
  };
  personal_history_update?: boolean;
  /** Live "Patient's Reported Information" delta (chief complaint, duration,
   *  mode of onset, and the History section fields), published directly on
   *  change — drives PatientInformationCard in a live-only (no kiosk)
   *  session, where it would otherwise stay empty until the post-session
   *  summary. Snake_case keys match the backend directly. */
  history_information?: {
    chief_complaint?: string;
    duration?: string;
    mode_of_onset?: string;
    history?: string;
    past_illness?: string;
    treatment_history?: string;
    family_history?: string;
    family_social_occupational?: string;
    current_medication?: string;
  };
  history_information_update?: boolean;
}

/**
 * Ayurveda-specific types now live in `@/ayurveda/types/ayurveda`.
 * Re-exported here for backward compatibility with existing imports.
 */
export type {
  ReferenceSlokaItem,
  AyurvedaData,
  ClinicalAssessmentEntry,
  ClinicalAssessmentParam,
  ClinicalAssessmentSource,
  RegistrationInfo,
  PersonalHistory,
  PrescribedMedicine,
};

/**
 * Vital Signs
 */
export interface VitalSigns {
  bloodPressure?: string; // e.g., "120/80"
  heartRate?: number; // bpm
  temperature?: number; // Fahrenheit
  respiratoryRate?: number; // breaths per minute
  oxygenSaturation?: number; // percentage
  bloodSugar?: number; // mg/dL
  weight?: number; // kg
  height?: number; // cm
  hemoglobin?: number; // g/dL
  cholesterol?: number; // mg/dL
}

/**
 * Transcription segment
 */
export interface TranscriptionSegment {
  id: string;
  timestamp: number;
  speaker?: 'doctor' | 'patient' | 'system';
  text: string;
  confidence?: number;
  isFinal: boolean;
}

/**
 * CDSS Suggestion
 */
export interface CDSSSuggestion {
  id: string;
  type: 'diagnosis' | 'investigation' | 'treatment' | 'medication' | 'warning';
  title: string;
  description: string;
  confidence?: number;
  reasoning?: string;
  references?: string[];
}

/**
 * Consultation status
 */
export type ConsultationStatus = 'draft' | 'in-progress' | 'completed' | 'cancelled';

// AyurvedaData is now defined in `@/ayurveda/types/ayurveda` and re-exported
// at the top of this file for backward compatibility.

/**
 * PA Kiosk Data Section - A single card's data in the grid
 */
export interface PADataSection {
  id: string;
  title: string;
  items: string[];
}

/**
 * PA Kiosk structured data from the Physician Assistant / Kiosk submission
 */
export interface PAKioskData {
  sections: PADataSection[];
}

/**
 * AI Analysis Field - Common structure for most fields
 */
export interface AIAnalysisField {
  value: string | string[];
  timestamp: string;
  explanation: string;
}

/**
 * Medication from AI Analysis
 */
export interface AIMedication {
  name: AIAnalysisField;
  dosage: AIAnalysisField;
  period: AIAnalysisField;
  dosage_form: AIAnalysisField;
}

/**
 * Radiology from AI Analysis
 */
export interface AIRadiology {
  modality: AIAnalysisField;
  service_procedure: AIAnalysisField;
}

/**
 * Rehabilitation from AI Analysis
 */
export interface AIRehabilitation {
  therapy_type: AIAnalysisField;
  string_of_sessions: AIAnalysisField;
}

/**
 * Clinical Procedure from AI Analysis
 */
export interface AIClinicalProcedure {
  name: string;
  description?: string;
  indication?: string;
  [key: string]: unknown; // Allow additional fields from backend
}

/**
 * Better Medication Suggestion from CDSS
 * Badge: Yellow - "Better Medication"
 */
export interface BetterMedicationSuggestion {
  medication: string;
  medication_id: string;
  explanation: string;
  replace_with: string;
  replace_with_medication_id: string;
  source_url?: string;
}

/**
 * Drug Interaction Alert from CDSS
 * Badge: Red - "Drug Interaction"
 */
export interface DrugInteractionAlert {
  medication: string;
  medication_id: string;
  explanation: string;
  replace_with: string;
  replace_with_medication_id: string;
  conflicting_with: string;
  conflicting_with_medication_id: string;
  suggested_medications?: Array<{
    medication: string;
    replace_with_medication_id: string;
    explanation: string;
    source_url: string;
  }>;
  source_url?: string;
}

/**
 * Missed Symptom Alert from CDSS
 * Badge: Yellow - "Missed Symptoms"
 */
export interface MissedSymptomAlert {
  reason: string;
  symptom: string;
  symptom_timestamp: string;
  replace_with: string;
  replace_with_medication_id: string;
  suggested_medications?: Array<{
    medication: string;
    replace_with_medication_id: string;
    explanation: string;
    source_url: string;
  }>;
  severity: string;
  explanation: string;
  source_url?: string;
}

/**
 * Allergy Conflict Alert from CDSS
 * Badge: Red - "Allergy"
 */
export interface AllergyConflictAlert {
  medication: string;
  medication_id: string;
  allergy: string;
  allergy_timestamp: string;
  conflicting_medication: string;
  conflicting_medication_timestamp: string;
  severity: string;
  replace_with: string;
  replace_with_medication_id: string;
  explanation: string;
  source_url?: string;
  /** Confidence the backend has in this alert (e.g. "high" | "medium" | "low"). */
  evidence_confidence?: string;
  /** The patient/physician quote the allergy was derived from. */
  source_phrase?: string;
}

/**
 * Who stated a summary field's content — distinguishes the patient's own
 * words from the physician's assessment/orders. `null` when the field has
 * no content yet (e.g. an empty meal_plan).
 */
export type SummaryFieldSource = 'patient_stated' | 'physician_stated' | null;

/**
 * Modern (2026) summary field envelope — every top-level field in the new
 * backend schema is wrapped this way, whether its `value` is a plain bullet
 * list (string[]) or a list of structured entries (medications, family
 * history, physical exam, assessment/diagnosis).
 */
export interface SummaryFieldV2<T = string> {
  value: T[];
  /** Transcript time range this content was captured from, e.g. "0:12 - 0:45". */
  timestamp: string;
  source: SummaryFieldSource;
  /** The patient/physician's own words the value was derived from. */
  source_phrase: string;
  explanation: string;
}

/** One `family_history` entry — unchanged shape, now always array-native. */
export interface FamilyHistoryEntry {
  relation: string;
  condition: string;
  notes?: string;
}

/** One `physical_examination` entry — covers both vitals and exam findings. */
export interface PhysicalExamEntry {
  /** "vital-signs" | "exam" | other backend-defined grouping. */
  system: string;
  type: string;
  value: string;
  notes?: string;
}

/** One `assessment_and_diagnosis` entry. */
export interface AssessmentDiagnosisEntry {
  diagnosis: string;
  notes?: string;
}

/** One `medications` entry in the modern schema. */
export interface SummaryMedicationEntry {
  medication: string;
  dosage: { value: string; unit: string };
  frequency: string;
  timing: string[];
  duration: string;
  medicine_type: string;
  intake_type: string;
  relation_with_meal: string;
  composition: string;
  additional_info: string;
  dosing_type: 'scheduled' | 'sos' | string;
  sos_trigger: string;
  max_per_day: string;
}

/**
 * Pranik CDSS Suggestions (Extended)
 */
export interface PranikCDSS {
  betterMedicationSuggestions: {
    value: BetterMedicationSuggestion[];
    timestamp: string;
    explanation: string;
  };
  drugInteractionAlerts: {
    value: DrugInteractionAlert[];
    timestamp: string;
    explanation: string;
  };
  missedSymptomAlerts?: {
    value: MissedSymptomAlert[];
    timestamp: string;
    explanation: string;
  };
  allergyConflictAlerts?: {
    value: AllergyConflictAlert[];
    timestamp: string;
    explanation: string;
  };
}

/**
 * Session Summary Field - Common structure for summary fields
 * Note: Backend may send either nested format { value, timestamp, explanation }
 * or direct string values. Use helper functions to extract.
 */
export interface SessionSummaryField {
  value: string | string[];
  timestamp: string;
  explanation: string;
}

/**
 * Flexible Session Summary Field - allows direct string or nested object
 */
export type FlexibleSessionSummaryField = string | string[] | SessionSummaryField;

/**
 * Medication item in SessionSummary medications field
 */
export interface SessionMedicationItem {
  name: string;
  dosage?: string;
  period?: string;
  form?: string;
}

/**
 * Session Medications Field - specific structure for medications
 * Note: value can be either a string (comma-separated) or an array of items
 */
export interface SessionMedicationsField {
  value: string | SessionMedicationItem[];
  timestamp: string;
  explanation: string;
}

/**
 * Flexible Medications Field - allows direct string/array or nested object
 */
export type FlexibleMedicationsField = string | string[] | SessionMedicationsField;

/**
 * Session Summary from LiveKit agent via session_info topic.
 *
 * The Ayurveda backend agent returns BOTH generic alias fields (consumed by the
 * shared Consultation/Case Sheet flow) AND Ayurveda-native fields (for
 * Ayurveda-specific rendering). All fields are typed as `unknown` because the
 * backend may send direct strings, direct arrays, or nested
 * `{ value, timestamp, explanation }` objects — use `getSessionFieldValue` /
 * `extractSessionField` helpers to extract values safely.
 */
export interface SessionSummary {
  // Generic alias fields (guaranteed by both modern-medicine and Ayurveda agents)
  chief_complaint: unknown;
  history_of_present_illness: unknown;
  past_medical_surgical_history: unknown;
  /** @deprecated Merged into past_medical_surgical_history by the backend; kept optional for older stored sessions. */
  medication_history?: unknown;
  allergies: unknown;
  family_history: unknown;
  social_history: unknown;
  review_of_systems: unknown;
  physical_examination: unknown;
  assessment_and_diagnosis: unknown;
  investigations_orders: unknown;
  treatment_plan_management: unknown;
  medications: unknown;
  /** @deprecated Merged into treatment_plan_management by the backend; kept optional for older stored sessions. */
  follow_up_plan?: unknown;
  patient_summary: unknown;
  /** Structured diet/meal plan bullets, present for departments that capture one. */
  meal_plan?: unknown;
  diet_advice?: unknown;
  pranik_cdss?: PranikCDSS;
  diet_recommendations?: unknown;
  lifestyle_recommendations?: unknown;
  viruddha_ahaara?: unknown;
  suggestive_slokas?: ReferenceSlokaItem[];

  // Ayurveda-native fields (present only for Ayurveda consultations).
  // These are aliases the backend also maps into the generic fields above,
  // but are kept here so the frontend can fall back to them if a generic
  // alias is empty, and so Ayurveda-specific UI can render them directly.
  symptoms?: unknown;
  dosha_profile?: unknown;
  desa_information?: unknown;
  ayurveda_context?: unknown;
  commonly_eaten_food?: unknown;
  lifestyle?: unknown;
  known_diseases_history?: unknown;
  known_diseases?: unknown[];
  prescribed_medicines?: unknown;
  ayurvedic_medicines?: unknown;
  diet_modifications?: unknown;
  lifestyle_modifications?: unknown;
  viruddha_ahara_detected?: unknown;
  assessment?: unknown;
  treatment_plan?: unknown;
  relevant_investigations?: unknown;
  ayurveda_cdss?: unknown;
  medical_history?: unknown;
  co_morbidity?: unknown;
  // Additional Ayurveda-native fields confirmed from a real backend session
  // summary payload — chief complaint duration/mode of onset, the History
  // cluster, Personal History, Astha Sthaana Pareeksha, Diagnosis, and
  // Treatment Principle.
  chief_complaint_duration?: unknown;
  chief_complaint_mode_of_onset?: unknown;
  past_illness?: unknown;
  treatment_history?: unknown;
  family_social_occupational?: unknown;
  personal_history?: unknown;
  astha_sthaana_pareeksha?: unknown;
  diagnosis?: unknown;
  treatment_principle?: unknown;
  /** The Vaidya's own literal quote for treatment principle, distinct from
   *  the AI-classified `treatment_principle` tag list above. */
  treatment_principle_stated?: unknown;
}

/**
 * Session Info Response from LiveKit agent
 */
export interface SessionInfoResponse {
  success: boolean;
  summary: SessionSummary;
  summary_file?: string;
}

/**
 * Complete AI Analysis Result from audio-chunks endpoint
 */
export interface AIAnalysisResult {
  chief_complaint: AIAnalysisField;
  family_history?: AIAnalysisField;
  co_morbidity: AIAnalysisField;
  examination: AIAnalysisField;
  medical_history: AIAnalysisField;
  medication_history?: AIAnalysisField;
  diet_advice?: AIAnalysisField;
  history_of_present_illness: AIAnalysisField;
  diagnosis: AIAnalysisField;
  doctor_notes: AIAnalysisField;
  lab_tests: AIAnalysisField;
  investigations_orders?: AIAnalysisField;
  radiology: AIRadiology;
  clinical_procedures: AIClinicalProcedure[];
  rehabilitation: AIRehabilitation;
  medications: AIMedication[];
  patient_summary: AIAnalysisField;
  follow_up_details: AIAnalysisField;
  pranik_cdss: PranikCDSS;
}

/**
 * Consultation data
 */
export interface Consultation {
  uuid: string;
  patientId: string;
  patientName: string;
  doctorId: string;
  doctorName: string;
  status: ConsultationStatus;
  chiefComplaint?: string;
  vitals?: VitalSigns;
  transcription?: TranscriptionSegment[];
  analysis?: {
    chiefComplaint?: string;
    historyOfPresentIllness?: string;
    reviewOfSystems?: string;
    physicalExamination?: string;
    assessment?: string;
    plan?: string;
  };
  aiAnalysis?: AIAnalysisResult;
  cdssSuggestions?: CDSSSuggestion[];
  audioFileUrl?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

/**
 * WebSocket connection status
 */
export type WebSocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

/**
 * WebSocket message types
 */
export type WebSocketMessageType =
  | 'audio_chunk'
  | 'transcription'
  | 'analysis'
  | 'analysis_complete'
  | 'processing'
  | 'cdss'
  | 'error'
  | 'ping'
  | 'pong'
  | 'connection_status'
  | 'ready'
  | 'chunk_batch'
  | 'chunk_received'
  | 'stop_recording'
  | 'end_session';

/**
 * Base WebSocket message
 */
export interface WebSocketMessage {
  type: WebSocketMessageType;
  timestamp: number;
}

/**
 * Audio chunk message (client -> server)
 */
export interface AudioChunkMessage extends WebSocketMessage {
  type: 'audio_chunk';
  data: ArrayBuffer | Blob;
  metadata?: {
    sampleRate?: number;
    channels?: number;
    format?: string;
  };
}

/**
 * Transcription message (server -> client)
 */
export interface TranscriptionMessage extends WebSocketMessage {
  type: 'transcription';
  segment: TranscriptionSegment;
}

/**
 * Analysis message (server -> client)
 */
export interface AnalysisMessage extends WebSocketMessage {
  type: 'analysis';
  data: Consultation['analysis'];
}

/**
 * Analysis complete message (server -> client) - final analysis from audio-chunks endpoint
 */
export interface AnalysisCompleteMessage extends WebSocketMessage {
  type: 'analysis_complete';
  session_id: string;
  result: AIAnalysisResult;
  chunks_processed: number;
  analysis_time: number;
  audio_filename?: string;
  audio_blob_url?: string;
}

/**
 * Processing status message (server -> client)
 */
export interface ProcessingMessage extends WebSocketMessage {
  type: 'processing';
  message: string;
}

/**
 * CDSS message (server -> client)
 */
export interface CDSSMessage extends WebSocketMessage {
  type: 'cdss';
  suggestions: CDSSSuggestion[];
}

/**
 * Error message (server -> client)
 */
export interface ErrorMessage extends WebSocketMessage {
  type: 'error';
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/**
 * Connection status message
 */
export interface ConnectionStatusMessage extends WebSocketMessage {
  type: 'connection_status';
  status: WebSocketStatus;
}

/**
 * Ready message (server -> client) - indicates session is ready to receive audio
 */
export interface ReadyMessage extends WebSocketMessage {
  type: 'ready';
  session_id: string;
}

/**
 * Chunk batch acknowledgment message (server -> client)
 */
export interface ChunkBatchMessage extends WebSocketMessage {
  type: 'chunk_batch';
  count: number;
}

/**
 * Chunk received acknowledgment message (server -> client)
 */
export interface ChunkReceivedMessage extends WebSocketMessage {
  type: 'chunk_received';
}

/**
 * Union type for all WebSocket messages
 */
export type WebSocketIncomingMessage =
  | TranscriptionMessage
  | AnalysisMessage
  | AnalysisCompleteMessage
  | ProcessingMessage
  | CDSSMessage
  | ErrorMessage
  | ConnectionStatusMessage
  | ReadyMessage
  | ChunkBatchMessage
  | ChunkReceivedMessage;

/**
 * Audio recording state
 */
export interface AudioRecordingState {
  isRecording: boolean;
  isPaused: boolean;
  duration: number; // seconds
  startTime?: number;
  pauseTime?: number;
}

/**
 * Consultation creation request
 */
export interface ConsultationCreateRequest {
  patientId: string;
  chiefComplaint?: string;
  vitals?: VitalSigns;
}

/**
 * Consultation update request
 */
export interface ConsultationUpdateRequest {
  chiefComplaint?: string;
  vitals?: VitalSigns;
  status?: ConsultationStatus;
}

/**
 * Consultation list item
 */
export interface ConsultationListItem {
  uuid: string;
  patientId: string;
  patientName: string;
  status: ConsultationStatus;
  chiefComplaint?: string;
  createdAt: string;
  completedAt?: string;
}

/**
 * Helper to format duration
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Helper to format vital signs for display
 */
export function formatVitals(vitals: VitalSigns): string[] {
  const formatted: string[] = [];

  if (vitals.bloodPressure) {
    formatted.push(`- B.P.: ${vitals.bloodPressure} mmHg`);
  }
  if (vitals.heartRate) {
    formatted.push(`- Heart Rate: ${vitals.heartRate} bpm`);
  }
  if (vitals.temperature) {
    formatted.push(`- Temperature: ${vitals.temperature} °F`);
  }
  if (vitals.respiratoryRate) {
    formatted.push(`- Respiratory Rate: ${vitals.respiratoryRate} /min`);
  }
  if (vitals.oxygenSaturation) {
    formatted.push(`- SpO2: ${vitals.oxygenSaturation} %`);
  }
  if (vitals.bloodSugar) {
    formatted.push(`- Blood Sugar: ${vitals.bloodSugar} mg/dL`);
  }
  if (vitals.hemoglobin) {
    formatted.push(`- Hemoglobin: ${vitals.hemoglobin} g/dL`);
  }
  if (vitals.cholesterol) {
    formatted.push(`- Cholesterol: ${vitals.cholesterol} mg/dL`);
  }
  if (vitals.weight) {
    formatted.push(`- Weight: ${vitals.weight} kg`);
  }
  if (vitals.height) {
    formatted.push(`- Height: ${vitals.height} cm`);
  }

  return formatted;
}

/**
 * Helper to get consultation status label
 */
export function getConsultationStatusLabel(status: ConsultationStatus): string {
  const labels: Record<ConsultationStatus, string> = {
    draft: 'Draft',
    'in-progress': 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
  };
  return labels[status];
}

/**
 * Helper to get consultation status variant
 */
export function getConsultationStatusVariant(
  status: ConsultationStatus
): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  const variants: Record<
    ConsultationStatus,
    'default' | 'primary' | 'success' | 'warning' | 'error'
  > = {
    draft: 'default',
    'in-progress': 'primary',
    completed: 'success',
    cancelled: 'warning',
  };
  return variants[status];
}

/**
 * Helper to get CDSS suggestion icon
 */
export function getCDSSSuggestionVariant(
  type: CDSSSuggestion['type']
): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' {
  const variants: Record<
    CDSSSuggestion['type'],
    'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
  > = {
    diagnosis: 'primary',
    investigation: 'info',
    treatment: 'success',
    medication: 'default',
    warning: 'error',
  };
  return variants[type];
}
